import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Zap, ShoppingBag, MessageCircle, Award, Package, Video, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/prefs";
import { SectionHeader, Button } from "@/components/brand";
import { ClassCard, TrainerPortraitCard, CategoryTile, ChallengeCard } from "@/components/cards";
import { canJoin, formatDayTime } from "@/lib/format";

const QUICK = [
  ["quick-fitpass", Zap, "quick.fitpass", "/plans"],
  ["quick-shop", ShoppingBag, "quick.shop", "/shop"],
  ["quick-messages", MessageCircle, "quick.messages", "/messages"],
  ["quick-challenges", Award, "quick.challenges", "/challenges"],
  ["quick-orders", Package, "quick.orders", "/orders"],
];

export default function Discover() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const cats = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const classes = useQuery({ queryKey: ["classes", {}], queryFn: () => api("/classes?limit=12") });
  const trainers = useQuery({ queryKey: ["trainers", {}], queryFn: () => api("/trainers") });
  const bookings = useQuery({ queryKey: ["bookings", "me"], queryFn: () => api("/bookings/me") });
  const challenges = useQuery({ queryKey: ["challenges"], queryFn: () => api("/challenges") });

  const next = (bookings.data ?? []).filter((b) => b.status === "confirmed" && new Date(b.start_time).getTime() > Date.now() - b.duration_min * 60000)[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t("discover.morning") : hour < 17 ? t("discover.afternoon") : t("discover.evening");

  return (
    <div data-testid="discover-screen" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl text-ink tracking-tight">{greeting}, <span className="text-brand">{user?.name?.split(" ")[0]}</span></h1>
        <p className="font-body text-mutedgrey mt-0.5">{t("discover.letsMove")}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {QUICK.map(([tid, Icon, label, path]) => (
          <button key={tid} data-testid={tid} onClick={() => navigate(path)} className="inline-flex shrink-0 items-center gap-1.5 h-9 px-4 rounded-pill bg-surface2 border border-line font-body font-semibold text-[13px] text-ink hover:bg-brand-soft transition-colors">
            <Icon size={16} className="text-brand" />{t(label)}
          </button>
        ))}
      </div>

      {next ? (
        <div className="rounded-xl2 bg-brand p-5 text-white" data-testid="discover-next-card">
          <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-white animate-pulse" /><span className="font-body font-semibold text-[11px] tracking-widest">{t("discover.nextWorkout")}</span></div>
          <div className="font-display text-2xl mt-2 truncate">{next.title}</div>
          <div className="font-body text-sm text-white/90 mt-0.5">with {next.trainer_name} · {formatDayTime(next.start_time)} · {next.duration_min} min</div>
          <div className="mt-4">
            {canJoin(next.start_time, next.duration_min)
              ? <Button testID="discover-join-next" title={t("discover.joinClass")} icon={Video} variant="dark" small onClick={() => navigate(`/live/${next.id}`)} />
              : <Button testID="discover-view-next" title={t("discover.viewBooking")} variant="dark" small onClick={() => navigate(`/booking/${next.id}`)} />}
          </div>
        </div>
      ) : (
        <div className="rounded-xl2 bg-brand-soft p-6">
          <p className="font-body font-semibold text-[11px] tracking-widest text-brand">LIVE CLASSES. PERSONAL TRAINING.</p>
          <h2 className="font-display text-2xl text-ink mt-2 max-w-md">Train Live With <span className="text-brand">Real</span> Fitness Professionals</h2>
          <div className="flex gap-3 mt-4">
            <Button testID="discover-find-class" title={t("welcome.findClass")} icon={ArrowRight} small onClick={() => navigate("/classes")} />
            <Button testID="discover-find-trainer" title={t("welcome.findTrainer")} variant="outline" small onClick={() => navigate("/trainers")} />
          </div>
        </div>
      )}

      <div>
        <SectionHeader title={t("discover.categories")} />
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {(cats.data ?? []).map((c) => <CategoryTile key={c.key} name={c.name} image={c.image} icon={c.icon} testID={`cat-${c.key}`} onClick={() => navigate(`/classes?category=${c.key}`)} />)}
        </div>
      </div>

      {(challenges.data ?? []).length > 0 ? (
        <div>
          <SectionHeader title={t("discover.challenges")} subtitle={t("discover.challengesSub")} action={t("common.viewAll")} to="/challenges" testID="discover-view-all-challenges" />
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">{(challenges.data ?? []).slice(0, 6).map((c) => <ChallengeCard key={c.id} item={c} compact />)}</div>
        </div>
      ) : null}

      <div>
        <SectionHeader title={t("discover.upcoming")} subtitle={t("discover.upcomingSub")} action={t("common.viewAll")} to="/classes" testID="discover-view-all-classes" />
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">{(classes.data ?? []).slice(0, 8).map((c) => <ClassCard key={c.id} item={c} compact />)}</div>
      </div>

      <div>
        <SectionHeader title={t("discover.topTrainers")} subtitle={t("discover.topTrainersSub")} action={t("common.viewAll")} to="/trainers" testID="discover-view-all-trainers" />
        <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">{(trainers.data ?? []).map((tr) => <TrainerPortraitCard key={tr.user_id} item={tr} />)}</div>
      </div>
    </div>
  );
}
